import {
  Anchor,
  Button,
  Checkbox,
  Group,
  LoadingOverlay,
  Modal,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { useNavigate } from "react-router-dom";
import { useMemo, useState } from "react";
import { useLanguage } from "../../../stores/LanguageStore";
import { useSessionStore } from "../../../stores/UserStore";
import { signup } from "../../../api/AuthAPI";
import PasswordField from "../../../components/View/PasswordField";
import OAuthButtons from "../login/OAuthButtons";
import { notifications } from "@mantine/notifications";
import { modals } from "@mantine/modals";
import PhoneNumberInput from "../../../components/Selectors/PhoneNumberInput";
import MobileGuard from "../MobileGuard";
import { appHost } from "../../../functions/appHost";

const SignupForm = () => {
  const lang = useLanguage((s) => s.language);
  const reseller = useSessionStore((s) => s.reseller);

  //#region Signup
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({
    email: false,
    password: false,
    password_confirm: false,
    phone: false,
    fName: false,
    lName: false,
    unknown: false,
  });
  const [pwdError, setPwdError] = useState(true);
  const [mobileGuard, setMobileGuard] = useState(false);

  const verifPhone = useMemo(() => {
    return reseller?.options?.find(
      (x) => x.name === "SmsVerificationOnRegister"
    )?.active;
  }, [reseller]);

  const handlePhoneError = () => {
    setErrors({ phone: true });
    document.getElementById("phone").focus();
    setLoading(false);
  };
  const handlePwdError = () => {
    setErrors({ password: true });
    document.getElementById("pwd").focus();
    setLoading(false);
  };
  const handleEmailError = () => {
    setErrors({ email: true });
    document.getElementById("email").focus();
    setLoading(false);
  };
  const handleFNameError = () => {
    setErrors({ fName: true });
    document.getElementById("first_name").focus();
    setLoading(false);
  };
  const handleLNameError = () => {
    setErrors({ lName: true });
    document.getElementById("last_name").focus();
    setLoading(false);
  };
  const handleUnknownError = () => {
    setErrors({ unknown: true });
    setLoading(false);
  };

  const navigate = useNavigate();

  const handleSignup = (e) => {
    e.preventDefault();
    setErrors({});

    // Params
    const params = new URLSearchParams(location.search);
    let ref = params.get("ref");
    let team_code = params.get("team_code");
    let organization_code = params.get("organization_code");

    // Data
    let formData = new FormData(e.target);
    const data = Object.fromEntries(formData);

    // Honeypot
    if (data?.xyz) {
      return;
    }
    if (data?.first_name === "sdd") {
      return;
    }

    data.domain_saas =
      reseller?.domain_saas3 === "application.magileads.io"
        ? null
        : reseller?.domain_saas3 || reseller?.domain_saas2 || null;
    data.email_validation_url_success = `https://${appHost(reseller)}/success/account?lang=${
      lang.id
    }&refresh_token=%refresh%&access_token=%access%`;
    data.email_validation_url_error = `https://${appHost(reseller)}/email-not-validated?lang=${lang.id}`;
    data.language = lang.id;
    data.referred_user_id = parseInt(ref) || null;
    data.team_codes = team_code ? [team_code] : null;
    data.organization_codes = organization_code ? [organization_code] : null;
    data.show_onboarding = true;

    setErrors({
      email: false,
      password: false,
      password_confirm: false,
      phone: false,
      fName: false,
      lName: false,
      unknown: false,
    });

    // if (data?.password_confirm !== data.password) {
    //   return handlePwdConfirmError();
    // }

    if (pwdError) {
      return handlePwdError();
    } else {
      setLoading(true);
      signup(data)
        .then(() => {
          setLoading(false);

          if (window.innerWidth < 768) {
            return setMobileGuard(true);
          } else {
            // Google Tag
            if (reseller.name === "Magileads") {
              const script = document.createElement("script");
              script.innerHTML = `
              (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
              new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
              j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
              'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
              })(window,document,'script','dataLayer','GTM-5VWS3JBC');
              `;

              script.type = "text/javascript";
              document.head.appendChild(script);
            }

            notifications.show({
              message: lang.signup.success,
              color: "green",
              position: window.innerWidth < 768 ? "top-left" : undefined,
            });

            if (verifPhone) {
              navigate(
                `/signup-verification?phone=${data?.mobile_phone}&country=${data?.mobile_phone_country}`
              );
            } else {
              modals.open({
                modalId: "verif-email",
                title: lang.signup.signup_success.title,
                children: (
                  <Stack justify="center">
                    <Text size="lg">{lang.signup.signup_success.text}</Text>

                    <Button
                      size="lg"
                      onClick={() => {
                        navigate("/login");
                        modals.close("verif-email");
                      }}
                    >
                      {lang.signup.signup_success.button_label}
                    </Button>
                  </Stack>
                ),
              });
            }
          }
        })
        .catch((error) => {
          let data = error.response.data;
          setLoading(false);
          if (data.state_message === "empty_last_name") {
            return handleLNameError();
          }
          if (data.state_message === "empty_first_name") {
            return handleFNameError();
          }
          if (data.state_message === "incorrect_email") {
            return handleEmailError();
          }
          if (data.state_message === "mail_error") {
            return handleEmailError();
          }
          if (data.state_message === "email_used") {
            return handleEmailError();
          }
          if (data.state_message === "incorrect_mobile_phone") {
            return handlePhoneError();
          }
          if (data.state_message === "mobile_phone_used") {
            return handlePhoneError();
          } else {
            handleUnknownError();
          }
        });
    }
  };
  //#endregion
  const isMobile = window.innerWidth < 768;

  return (
    <Stack component={"form"} onSubmit={handleSignup} w={"100%"} mx={"auto"}>
      <Modal
        opened={mobileGuard}
        centered
        overlayProps={{ blur: 3 }}
        withCloseButton={false}
        closeOnClickOutside={false}
      >
        <MobileGuard isSignup={true} onClose={() => setMobileGuard(false)} />
      </Modal>
      <LoadingOverlay visible={loading} />

      <Group wrap="nowrap">
        <TextInput
          name="first_name"
          id="first_name"
          label={lang.signup.first_name}
          placeholder={lang.signup.first_name}
          required
          w={"100%"}
          size={isMobile ? "md" : "lg"}
          className={`animate__animated animate__fadeInUp`}
          style={{ animationDelay: ".1s" }}
        />

        <TextInput
          name="last_name"
          id="last_name"
          label={lang.signup.last_name}
          placeholder={lang.signup.last_name}
          required
          w={"100%"}
          size={isMobile ? "md" : "lg"}
          className={`animate__animated animate__fadeInUp`}
          style={{ animationDelay: ".2s" }}
        />
      </Group>

      <Group wrap={"nowrap"}>
        <TextInput
          name="email"
          id="email"
          label={lang.signup.mail}
          placeholder={lang.signup.mail}
          required
          error={errors.email}
          w={"100%"}
          type="email"
          size={isMobile ? "md" : "lg"}
          className={`animate__animated animate__fadeInUp`}
          style={{ animationDelay: ".3s" }}
        />
        <PhoneNumberInput
          defaultCountry={"FR"}
          name={"mobile_phone"}
          label={lang.profile.mobile_phone}
          required={verifPhone}
          w={"100%"}
          size={isMobile ? "md" : "lg"}
          className={`animate__animated animate__fadeInUp`}
          style={{ animationDelay: ".3s" }}
        />
      </Group>
      <PasswordField
        error={pwdError}
        setError={setPwdError}
        id={"pwd"}
        size={isMobile ? "md" : "lg"}
        className={`animate__animated animate__fadeInUp`}
        style={{ animationDelay: ".4s" }}
      />

      {(reseller?.name === "Magileads" || reseller?.terms_of_sale) && (
        <Checkbox
          name="cgv"
          required
          size={isMobile ? "md" : "lg"}
          defaultChecked
          label={
            <>
              {lang.signup.cgv_text1}{" "}
              <Anchor
                href={
                  reseller?.name === "Magileads"
                    ? "https://app.files-magileads.net/file/680F75109EA5D151824664/CGV_Magileads.pdf"
                    : reseller?.terms_of_sale
                }
                size={isMobile ? "md" : "lg"}
              >
                {lang.signup.cgv_text2} {reseller?.name}
              </Anchor>
            </>
          }
          className={`animate__animated animate__fadeInUp`}
          style={{ animationDelay: ".5s" }}
        />
      )}
      {/* Honeypot field (hidden from users) */}
      <input type="text" name="xyz" style={{ display: "none" }} />

      {/* <TextInput
        label={lang.signup.password_confirm}
        placeholder={lang.signup.password_confirm}
        required
        error={
          errors.password_confirm ? lang.signup.password_confirm_error : null
        }
        name="password_confirm"
        w={"100%"}
        id="pwd-confirm"
        type="password"
        size="lg"
        className={`animate__animated animate__fadeInUp`}
        style={{ animationDelay: ".3s" }}
      /> */}

      <Stack
        justify="center"
        w={250}
        mx={"auto"}
        style={{ zIndex: 8, position: "relative" }}
      >
        <Button
          type="submit"
          loading={loading}
          mt={"md"}
          w={"100%"}
          mx={"auto"}
          className={`animate__animated animate__fadeInUp`}
          style={{ animationDelay: ".5s" }}
          size={isMobile ? "md" : "lg"}
          color="primary"
          fz={18}
        >
          {lang.signup.button_label}
        </Button>

        <OAuthButtons
          className={`animate__animated animate__fadeIn`}
          style={{ animationDelay: ".6s" }}
          isMobile={isMobile}
        />
      </Stack>
    </Stack>
  );
};

export default SignupForm;
